import React, { useCallback, useMemo, useState } from "react";
import { Box, makeStyles, Typography } from "@material-ui/core";

import EditRevenueDialog from "./EditRevenueDialog";
import NewRevenueDialog from "./NewRevenueDialog";
import {
  FINANCIAL_REPORT_SUBTITLES,
  REPORT_ITEM_SUB_TYPE,
  REPORT_ITEM_TYPE
} from "../../../../../constants";
import isEqual from "../../../../../utils/isEqual";
import CustomCardHeader from "../../CustomCardHeader";
import { asCurrency } from "../../../../../utils";
import Button from "components/CustomButtons/Button";
import Edit from "@material-ui/icons/Edit";

const useStyles = makeStyles({
  actions: {
    margin: "0 0 0 5px",
    padding: 5,
    "& svg": {
      margin: 0
    }
  }
});
const OperatingIncome = ({
  reportID,
  data,
  defaultData,
  chapterID,
  year,
  createReportItem,
  updateReportItem,
  updateReportItems,
  updateReportItemsPut,
  initialReportItems,
  removeReportItem,
  onRefetch,
  onRedirect,
  onHelp,
  isEditable
}) => {
  const classes = useStyles();

  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);

  const items = useMemo(() => {
    return data.filter(
      item => item.reportItemSubTypeID === REPORT_ITEM_SUB_TYPE.OPERATING_INCOME
    );
  }, [data]);

  const defaultItems = useMemo(() => {
    return defaultData.filter(
      item => item.reportItemSubTypeID === REPORT_ITEM_SUB_TYPE.OPERATING_INCOME
    );
  }, [defaultData]);

  const presentedItems = items.length === 0 ? defaultItems : items;

  const isEqualToDefault = useMemo(
    () => isEqual(presentedItems, defaultItems),
    [presentedItems, defaultItems]
  );

  const onSaveEditDialog = useCallback(
    async ({ items, editedItems, removedItems }) => {
      if (reportID === "add") {
        const body = {
          chapterID,
          year,
          items: items.map(
            ({
              description,
              amount,
              reportItemSubTypeID,
              reportItemTypeID
            }) => ({
              description,
              amount: amount ? +amount : 0,
              staffPercent: 0,
              hoursPerWeek: 0,
              reportItemTypeID,
              reportItemSubTypeID
            })
          )
        };
        const _reportID = await initialReportItems(body);
        onRedirect(_reportID);
      } else {
        if (isEqualToDefault) {
          const body = items.map(
            ({
              description,
              amount,
              reportItemSubTypeID,
              reportItemTypeID
            }) => ({
              description,
              staffPercent: 0,
              hoursPerWeek: 0,
              amount: amount ? +amount : 0,
              reportID,
              reportItemTypeID,
              reportItemSubTypeID
            })
          );

          await updateReportItems(body);
        } else {
          var itemsToSend = [];
          itemsToSend = editedItems.map(
            ({
              id,
              description,
              amount,
              reportItemSubTypeID,
              reportItemTypeID
            }) => ({
              id,
              description,
              amount: +amount,
              staffPercent: 0,
              hoursPerWeek: 0,
              reportID,
              reportItemTypeID,
              reportItemSubTypeID
            })
          );

          await updateReportItemsPut(itemsToSend);

          await Promise.all(removedItems.map(({ id }) => removeReportItem(id)));
        }
      }
      onRefetch();
      setEditDialogOpen(false);
    },
    [reportID]
  );

  const onSaveCreateDialog = useCallback(
    async ({ description, amount, type }) => {
      const addedItem = {
        description,
        amount: +amount,
        staffPercent: 0,
        hoursPerWeek: 0,
        reportItemTypeID: REPORT_ITEM_TYPE.REVENUE,
        reportItemSubTypeID: type
      };
      const formItems = [
        ...defaultItems.map(
          ({ description, amount, reportItemSubTypeID, reportItemTypeID }) => ({
            description,
            staffPercent: 0,
            hoursPerWeek: 0,
            amount: amount ? +amount : 0,
            reportItemTypeID,
            reportItemSubTypeID
          })
        ),
        addedItem
      ];

      if (reportID === "add") {
        const _reportID = await initialReportItems({
          chapterID,
          year,
          items: formItems
        });
        onRedirect(_reportID);
      } else if (isEqualToDefault) {
        await updateReportItems(
          formItems.map(item => ({
            ...item,
            reportID
          }))
        );
      } else {
        await createReportItem({
          ...addedItem,
          reportID
        });
      }
      onRefetch();
      setCreateDialogOpen(false);
    },
    [reportID]
  );

  return (
    <Box mb={3}>
      <CustomCardHeader
        label={FINANCIAL_REPORT_SUBTITLES.Standard}
        onHelp={() =>
          onHelp(
            REPORT_ITEM_TYPE.REVENUE,
            REPORT_ITEM_SUB_TYPE.OPERATING_INCOME
          )
        }
        extraContent={
          presentedItems.length !== 0 &&
          isEditable && (
            <Button
              simple
              color="primary"
              className={classes.actions}
              onClick={() => setEditDialogOpen(true)}
            >
              <Edit />
            </Button>
          )
        }
      />
      {presentedItems.map(item => (
        <Box
          display="flex"
          justifyContent="space-between"
          px={1}
          py={0.5}
          key={`revenue-${item.id}`}
        >
          <Typography>{item.description}</Typography>
          <Typography>{asCurrency(item.amount || 0)}</Typography>
        </Box>
      ))}
      {isEditable && (
        <Box ml={3}>
          <a onClick={() => setCreateDialogOpen(true)}>
            <strong>{"Add an Income Source"}</strong>
          </a>
        </Box>
      )}
      <EditRevenueDialog
        open={editDialogOpen}
        title={FINANCIAL_REPORT_SUBTITLES.Standard}
        data={presentedItems}
        onClose={() => setEditDialogOpen(false)}
        onSave={onSaveEditDialog}
      />
      <NewRevenueDialog
        open={createDialogOpen}
        title={"Add an Income Source"}
        type={REPORT_ITEM_SUB_TYPE.OPERATING_INCOME}
        onClose={() => setCreateDialogOpen(false)}
        onSave={onSaveCreateDialog}
      />
    </Box>
  );
};

export default OperatingIncome;
